import { Component, ElementRef, ViewChild, Output, EventEmitter, Input, AfterViewInit, OnInit, OnDestroy } from '@angular/core';
import { filter, fromEvent, Subscription } from 'rxjs';
import { AppState } from '../../models/state';
import { GoogleMapService } from '../../services/google-map.service';
import { LocationService } from '../../services/location.service';
import { StateService } from '../../services/state.service';
import { UiService } from '../../services/ui.service';

@Component({
  selector: 'app-google-map-search-box',
  templateUrl: './google-map-search-box.component.html',
  styleUrls: ['./google-map-search-box.component.scss']
})
export class GoogleMapSearchBoxComponent implements AfterViewInit, OnInit, OnDestroy {

  @ViewChild('mapSearchField') searchField: ElementRef;
  @Input() radio: number;
  @Input() showSearchButton: boolean;
  @Input() focusOutEvent: boolean;


  @Output() onSearchLocation: EventEmitter<boolean>;
  @Output() onAddressChange: EventEmitter<{
    lat: string;
    lng: string;
    address: string;
    center: google.maps.LatLngLiteral;
    bounds: google.maps.LatLngBounds;
  }>;

  searchBox: google.maps.places.Autocomplete;
  bounds: google.maps.LatLngBounds;
  state: Partial<AppState>;
  lat: string;
  lng: string;
  address: string;
  center: google.maps.LatLngLiteral;
  touched: boolean;
  subs: Subscription;

  constructor(
    private ui: UiService,
    private stateService: StateService,
    private googleMapService: GoogleMapService,
    private location: LocationService
  ) {
    this.subs = new Subscription();
    this.onAddressChange = new EventEmitter();
    this.onSearchLocation = new EventEmitter();
    this.showSearchButton = true;
    this.focusOutEvent = true;
    this.state = this.stateService.getStateValue();
    this.address = this.state?.searchParams?.address;
    this.lat = this.state?.searchParams?.lat;
    this.lng = this.state?.searchParams?.lng;
  }

  ngOnInit(): void {
    this.subs.add(
      this.stateService.getState().subscribe(state => {
        this.state = state;
      })
    );
  }


  ngAfterViewInit(): void {
    this.searchBox = new google.maps.places.Autocomplete(this.searchField.nativeElement, {
      componentRestrictions: { country: 'es' },
      types: ['geocode'],
    });
    if (this.lat && this.lng) this.autoCompleteGoogleMap();
    this.placeChangeEventHandler();
  }

  ngOnDestroy(): void {
    this.subs.unsubscribe();
  }

  addressChange(): void {
    this.touched = true;
    this.lat = null;
    this.lng = null;
    this.addressChangeEvent();
  }

  getCurrentLocation(): void {
    this.ui.showSpinner();
    this.location.getCurrentLocation()
      .then(async (location) => {
        const geocoderResult = await this.location.getCurrentAddress(location);
        if (geocoderResult) {
          this.googleMapService.updateRadio(this.radio);
          const address = geocoderResult.formatted_address;
          this.searchField.nativeElement.value = address;
          this.lat = location?.lat?.toString();
          this.lng = location?.lng?.toString();
          this.address = address;
          this.center = location;
          const bounds = new google.maps.LatLngBounds();
          bounds.extend(geocoderResult?.geometry.location);
          this.bounds = bounds;
          this.addMapMarkers(location);
          this.addressChangeEvent();
        }
        this.ui.stopSpinner();
      }, () => this.ui.stopSpinner());
  }


  searchLocation() {
    this.onSearchLocation.emit(true);
  }

  onFocusOutEvent(e) {
    setTimeout(() => {
      if (this.addressInvalid() && this.address) this.autoSearch();
    }, 300);
  }

  async autoSearch() {
    let geoLocation: google.maps.GeocoderResult;
    let location: google.maps.LatLng;

    geoLocation = await this.location.getGeoLocation(this.address);

    if (!geoLocation) return;

    location = geoLocation.geometry.location;
    if (location) {
      this.lat = location?.lat()?.toString();
      this.lng = location?.lng()?.toString();
      this.address = geoLocation.formatted_address;
      this.addressChangeEvent();
    }
  }

  private placeChangeEventHandler() {
    this.searchBox.addListener('place_changed', () => {
      const place = this.searchBox.getPlace();
      const bounds = new google.maps.LatLngBounds();
      if (!place?.geometry?.location) return;
      this.lat = place?.geometry?.location?.lat()?.toString();
      this.lng = place?.geometry?.location?.lng()?.toString();
      this.address = this.searchField.nativeElement.value;
      if (place?.geometry?.viewport) bounds.union(place?.geometry.viewport);
      else bounds.extend(place?.geometry.location);
      this.bounds = bounds;
      if (place) this.stateService.updateState({ ...this.state, bounds });
      const location: google.maps.LatLngLiteral = { lat: Number(this.lat), lng: Number(this.lng) };
      this.addMapMarkers(location);
      this.addressChangeEvent();
    });
  }

  private addressInvalid(): boolean {
    return (!this.lat || !this.lng);
  }

  private autoCompleteGoogleMap() {
    this.bounds = this.state.bounds;
    setTimeout(() => {
      this.addMapMarkers({ lat: Number(this.lat), lng: Number(this.lng) });
      this.addressChangeEvent();
    });
  }

  private addressChangeEvent() {
    this.onAddressChange.emit({
      address: this.address,
      lat: this.lat,
      lng: this.lng,
      center: this.center,
      bounds: this.bounds
    });
    this.stateService.updateState({ ...this.state, bounds: this.bounds });
  }

  private addMapMarkers(location: google.maps.LatLngLiteral): void {
    this.googleMapService.addMarkers(
      {
        showModalCircle: true,
        locations: [location],
        center: location,
        icon: 'default-marker.png'
      },
      this.state,
      true);
  }

}
